import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { chatAction } from "store/chat-slice";
import { timeoutAction } from "store/timeout-slice";
import { playSound } from "util/MediaUtil";
import { useCallback } from "react";
import { messageAction } from "store/message-slice";
import { SmsMessageAPI } from "services/Chat/SmsMessageAPI";
import { uploadAsset } from "util/upload-media-helper";
import { getFileType, getFileTypeFromUrl } from "util/MediaUtil";
import { TELE_MESSAGE_STATUS } from "constants/ChatConstants";
import { MEDIA_TYPE } from "constants/ChatConstants";

const useSend = () => {
  const time = useSelector(
    (state) =>
      state.userDetails.details?.userSettings?.teleUserSettings
        ?.messageDelayTime
  );
  const outBoundNF = useSelector(
    (state) => state.userDetails.details.userSettings.outboundSounds
  );
  const dispatch = useDispatch();
  const token = useSelector((state) => state.user.token);

  const sendMessageFn = async ({
    payload,
    token,
    file,
    setSyncrModalOpen,
    setSyncrErrorMessage,
  }) => {
    const newPayload = {
      ...payload,
    };
    if (file && Boolean(file)) {
      let fileType = file.type ? getFileType(file.type) : MEDIA_TYPE.UNKNOWN;

      // If file.type is empty, get type from file extension
      if (fileType === MEDIA_TYPE.UNKNOWN && file.name) {
        const extension = file.name.split(".").pop(); // Get file extension
        fileType = getFileTypeFromUrl(extension);
      }

      if (fileType === MEDIA_TYPE.UNKNOWN) {
        // Show error toast
        Swal.fire({
          title: "Unsupported File Type",
          text: `The file "${file.name}" is not supported. Please select a valid media file. 
          Supported files types are: .jpg, .jpeg, .png, .gif, .mp4, .mp3, .wav, .pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .vcf`,
          icon: "error",
          confirmButtonText: "OK",
          confirmButtonColor: "#3085d6",
        });

        dispatch(
          messageAction.removeConversation({
            message: newPayload,
            recipientId: newPayload?.sendTo?.id,
          })
        );
        return; // Stop execution if file type is invalid
      }

      try {
        const uploaded = await uploadAsset({
          file,
          token,
          domain: payload.domain,
        });
        // when successfully uploaded, send message..
        const media = [
          {
            src: uploaded.asset.uri,
            uuid: uploaded.asset.uid,
          },
        ];
        newPayload["media"] = media;
        newPayload["messageAttchement"] = undefined;
        newPayload["message"] = Boolean(newPayload?.message)
          ? newPayload.message
          : "";
      } catch (ex) {
        // mark it as failed
        const newPayload = { ...payload };
        newPayload.status = TELE_MESSAGE_STATUS.failed;
        dispatch(
          messageAction.updateConversation({
            recipientId: newPayload?.sendTo.id,
            message: newPayload,
          })
        );
        return;
      }
    }
    SmsMessageAPI.createMessage({ payload: newPayload, token })
      .then((res) => {
        const messageSent = res.data;
        //// When message sent successfully
        try {
          playSound(outBoundNF[0]?.sourceUrl);
        } catch (e) {
          console.log(e);
        }
        dispatch(
          chatAction.updateLatestMessage({
            recipientId: messageSent?.sendTo.id,
            message: messageSent,
          })
        );
        dispatch(
          messageAction.updateConversation({
            recipientId: messageSent?.sendTo.id,
            message: messageSent,
          })
        );
      })
      .catch((e) => {
        console.log(e);
        const err = e?.response?.data;
        handleMessageFailure(err, setSyncrModalOpen, setSyncrErrorMessage);
      });
  };

  const send = useCallback(
    ({
      msgPayload,
      token,
      file = undefined,
      setSyncrModalOpen,
      setSyncrErrorMessage,
    }) => {
      if (msgPayload?.scheduledAt) {
        sendMessageFn({
          payload: msgPayload,
          token,
          file,
          setSyncrModalOpen,
          setSyncrErrorMessage,
        });
      } else {
        if (time === 0) {
          sendMessageFn({
            payload: msgPayload,
            token,
            file,
            setSyncrModalOpen,
            setSyncrErrorMessage,
          });
        } else {
          const id = setTimeout(
            () =>
              sendMessageFn({
                payload: msgPayload,
                token,
                file,
                setSyncrModalOpen,
                setSyncrErrorMessage,
              }),
            time ? time * 1000 : 5000
          );
          const payload = {
            messageId: msgPayload.tempId,
            recipientId: msgPayload.sendTo.id,
            timeoutObj: {
              id,
              trigger: () => (
                clearTimeout(id),
                sendMessageFn({
                  payload: msgPayload,
                  file,
                  token,
                  setSyncrModalOpen,
                  setSyncrErrorMessage,
                })
              ),
            },
          };
          dispatch(timeoutAction.addTimer(payload));
        }
      }
    },
    [time, outBoundNF]
  );

  const handleMessageFailure = useCallback(
    (sendMsgErr, setSyncrModalOpen, setSyncrErrorMessage) => {
      // const tmpErr = JSON.parse(sendMsgErr);
      let errPayload = sendMsgErr?.payload;
      if (sendMsgErr?.type === "whatsapp") {
        // Open pop up...
        Swal.fire({
          titleText: sendMsgErr?.message,
          text: "Would you like to send via SMS instead?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          customClass: {
            container: {
              zIndex: "10000 !important",
            },
          },
        })
          .then((result) => {
            if (result.isConfirmed) {
              errPayload = {
                ...errPayload,
                type: "SMS",
              };
              sendMessageFn({ payload: errPayload, token });
            } else {
              dispatch(
                messageAction.removeConversation({
                  message: errPayload,
                  recipientId: errPayload.sendTo.id,
                })
              );
            }
          })
          .catch((e) => {
            console.log(e);
          });
      } else if (sendMsgErr?.type === "syncr") {
        setSyncrModalOpen(true);
        setSyncrErrorMessage(sendMsgErr?.message);
        dispatch(
          messageAction.removeConversation({
            message: errPayload,
            recipientId: errPayload.sendTo.id,
          })
        );
      }
    },
    [token]
  );

  return { send };
};
export default useSend;